import { User, UserNotificationFlag } from "01Data/Account";
import {
  AndonDataType,
  AndonFilterSettings,
  AndonSortParam,
  BuildingInfo,
  Device,
  FilterItem,
  IFAndonData,
  LoginUser,
  Pico3,
  Sensor,
} from "01Data/DataTypes";
import { StateModel } from "99Tools/useModel";
import { CheckKizaiID } from "99Tools/utils";
import { Log } from "../Log";
import API from "./API";

export class AndonData
  extends StateModel<"ready" | "updating">
  implements IFAndonData
{
  public UserId: string = "";
  public buildings: BuildingInfo[] = [];
  public sensors: Sensor[] = [];
  public pico3: Pico3[] = [];
  public devices: Device[] = [];
  public log: Log = new Log();
  public filter: AndonFilterSettings = new AndonFilterSettings();
  public sortParamBuilding: AndonSortParam = {
    target: "buildings",
    param: "STATUS",
    order: "desc",
  };
  public sortParamSensor: AndonSortParam = {
    target: "sensors",
    param: "STATUS",
    order: "desc",
  };
  public sortParamPico3: AndonSortParam = {
    target: "pico3",
    param: "STATUS",
    order: "desc",
  };
  public errorMessage = "";

  public constructor() {
    super("ready");
  }

  public SetError(err: any, msg: string) {
    this.errorMessage = msg;
    console.log(this.errorMessage);
    console.error(err);
  }

  public Clear() {
    this.UserId = "";
    this.buildings = [];
    this.sensors = [];
    this.pico3 = [];
    this.devices = [];
    this.log = new Log();
  }

  public async Update() {
    if (this.getState() === "updating") {
      return;
    }
    await this.GetData(this.UserId).catch((err: any) => {
      this.SetError(err, "デバイス自動更新に失敗しました。");
    });
  }

  public async GetData(userId?: string) {
    if (this.getState() === "updating") {
      return;
    }
    this.setState("updating", false);
    const data = await API.GetAndonData();
    // データ準備
    const buildings = data.buildings.map((item: any) => new BuildingInfo(item));
    const pico3 = data.pico3.map((item: any) => new Pico3(item));
    const sensors = data.sensors.map((item: any) => new Sensor(item));
    // 建物一覧構築
    this.buildings = buildings.filter(
      (item: BuildingInfo) => item.UserId === this.UserId
    );
    // センサ一覧構築
    this.sensors = sensors.filter((item: Sensor) => {
      const b = this.buildings.filter(
        (b: BuildingInfo) => b.BuildingId === item.BuildingId
      );
      return 0 < b.length;
    });
    // PICO3一覧構築
    this.pico3 = pico3.filter((item: Pico3) => {
      const b = this.buildings.filter(
        (b: BuildingInfo) => b.BuildingId === item.BuildingId
      );
      return 0 < b.length;
    });
    // userIdがセットされている場合は表示設定を反映する
    if (userId) {
      const users = await User.GetUsers();
      const loginUser =
        users.find((user: User) => user.UserId === userId) ?? new User(null);
      await loginUser.GetDetails();
      // リンクしている建物のみ抽出
      this.buildings = this.buildings.filter((item: BuildingInfo) => {
        return (
          loginUser.LinkBuildings.find(
            (bid: string) => bid === item.BuildingId
          ) !== undefined
        );
      });
      // 表示設定しているセンサのみ抽出
      this.sensors = this.sensors.filter((item: Sensor) => {
        return (
          loginUser.SensorSettings.find((flags: UserNotificationFlag) => {
            return (
              item.BuildingId === flags.BuildingId &&
              item.SensorId === flags.SensorId &&
              flags.DisplayFlg === "true"
            );
          }) !== undefined
        );
      });
    }
    // デバイス一覧構築
    this.devices = [];
    this.sensors.forEach((item: Sensor) => {
      this.devices.push(
        new Device(
          {
            kind: CheckKizaiID(item.SensorId),
            id: item.SensorId,
            name: item.SensorName,
            StatusUpdatedAt: item.StatusUpdatedAt(),
            ComStatus: item.ComStatus,
            ImageURL: item.SensorImage,
          },
          new LoginUser(null)
        )
      );
    });
    this.pico3.forEach((item: Pico3) => {
      this.devices.push(
        new Device(
          {
            kind: "pico3",
            id: item.PicoId,
            name: item.PicoName,
            StatusUpdatedAt: item.StatusUpdatedAt(),
            ComStatus: item.ComStatus,
            ImageURL: item.PicoImage,
          },
          new LoginUser(null)
        )
      );
    });
    // 建物ステータス判定
    this.buildings.forEach((item: BuildingInfo) => {
      item.SetSensorStatus(this.sensors);
    });
    // 建物名取得(センサ)
    this.sensors.forEach((item: Sensor) => {
      item.SetBuildingName(this.buildings);
    });
    // 建物名取得(PICO3)
    this.pico3.forEach((item: Pico3) => {
      item.SetBuildingName(this.buildings);
    });
    // フィルター一覧構築
    this.filter.buildings.forEach((f) => {
      f.Add(new FilterItem("(すべて選択)", true));
    });
    this.buildings.forEach((item: BuildingInfo) => {
      this.filter.buildings[0].Add(
        new FilterItem(item.sensorStatus.textBuilding, true)
      ); // STATUS
      this.filter.buildings[1].Add(new FilterItem(item.BuildingName, true)); // 建物名
    });
    this.filter.sensors.forEach((f) => {
      f.Add(new FilterItem("(すべて選択)", true));
    });
    this.sensors.forEach((item: Sensor) => {
      this.filter.sensors[0].Add(
        new FilterItem(item.GetLeakageExpression().textSensor, true)
      ); // STATUS
      this.filter.sensors[1].Add(new FilterItem(item.SensorName, true)); // 端末名称
      this.filter.sensors[2].Add(new FilterItem(item.BuildingName, true)); // 建物名
      this.filter.sensors[3].Add(new FilterItem(item.LocationName, true)); // 設置場所
    });
    this.filter.pico3.forEach((f) => {
      f.Add(new FilterItem("(すべて選択)", true));
    });
    this.pico3.forEach((item: Pico3) => {
      this.filter.pico3[0].Add(
        new FilterItem(item.GetLeakageExpression().textPico3, true)
      ); // STATUS
      this.filter.pico3[1].Add(new FilterItem(item.PicoName, true)); // 端末名称
      this.filter.pico3[2].Add(new FilterItem(item.BuildingName, true)); // 建物名
      this.filter.pico3[3].Add(new FilterItem(item.LocationName, true)); // 設置場所
    });
    // フィルタリング
    this.ApplyFilter("buildings");
    this.ApplyFilter("sensors");
    this.ApplyFilter("pico3");
    // ソート
    this.buildings.forEach(
      (item: BuildingInfo, index: number) => (item.originalOrder = index)
    );
    this.Sort(this.sortParamBuilding);
    this.sensors.forEach(
      (item: Sensor, index: number) => (item.originalOrder = index)
    );
    this.Sort(this.sortParamSensor);
    this.pico3.forEach(
      (item: Pico3, index: number) => (item.originalOrder = index)
    );
    this.Sort(this.sortParamPico3);

    this.setState("ready", true);
  }

  public ResetFilter(target: AndonDataType, flag: boolean, colIndex: number) {
    switch (target) {
      case "buildings":
        this.filter.buildings[colIndex].items.forEach((item) => {
          item.bShow = flag;
        });
        this.ApplyFilter("buildings");
        break;
      case "sensors":
        this.filter.sensors[colIndex].items.forEach((item) => {
          item.bShow = flag;
        });
        this.ApplyFilter("sensors");
        break;
      case "pico3":
        this.filter.pico3[colIndex].items.forEach((item) => {
          item.bShow = flag;
        });
        this.ApplyFilter("pico3");
        break;

      default:
        break;
    }
  }

  public ApplyFilter(target: AndonDataType) {
    if (target === "buildings") {
      this.buildings.forEach(
        (building: BuildingInfo) => (building.bShowTop = true)
      ); // リセット
      this.buildings.forEach((building: BuildingInfo) => {
        let found = this.filter.buildings[0].items.find(
          (item: FilterItem) => item.name === building.sensorStatus.textBuilding
        );
        if (found && !found.bShow) {
          building.bShowTop = false;
          return;
        }
        found = this.filter.buildings[1].items.find(
          (item: FilterItem) => item.name === building.BuildingName
        );
        if (found && !found.bShow) {
          building.bShowTop = false;
          return;
        }
      });
      return;
    }
    if (target === "sensors") {
      this.sensors.forEach((sensor: Sensor) => (sensor.bShowTop = true)); // リセット
      this.sensors.forEach((sensor: Sensor) => {
        let found = this.filter.sensors[0].items.find(
          (item: FilterItem) =>
            item.name === sensor.GetLeakageExpression().textSensor
        );
        if (found && !found.bShow) {
          sensor.bShowTop = false;
          return;
        }
        found = this.filter.sensors[1].items.find(
          (item: FilterItem) => item.name === sensor.SensorName
        );
        if (found && !found.bShow) {
          sensor.bShowTop = false;
          return;
        }
        found = this.filter.sensors[2].items.find(
          (item: FilterItem) => item.name === sensor.BuildingName
        );
        if (found && !found.bShow) {
          sensor.bShowTop = false;
          return;
        }
        found = this.filter.sensors[3].items.find(
          (item: FilterItem) => item.name === sensor.LocationName
        );
        if (found && !found.bShow) {
          sensor.bShowTop = false;
          return;
        }
      });
      return;
    }
    if (target === "pico3") {
      this.pico3.forEach((pico3: Pico3) => (pico3.bShowTop = true)); // リセット
      this.pico3.forEach((pico3: Pico3) => {
        let found = this.filter.pico3[0].items.find(
          (item: FilterItem) =>
            item.name === pico3.GetLeakageExpression().textPico3
        );
        if (found && !found.bShow) {
          pico3.bShowTop = false;
          return;
        }
        found = this.filter.pico3[1].items.find(
          (item: FilterItem) => item.name === pico3.PicoName
        );
        if (found && !found.bShow) {
          pico3.bShowTop = false;
          return;
        }
        found = this.filter.pico3[2].items.find(
          (item: FilterItem) => item.name === pico3.BuildingName
        );
        if (found && !found.bShow) {
          pico3.bShowTop = false;
          return;
        }
        found = this.filter.pico3[3].items.find(
          (item: FilterItem) => item.name === pico3.LocationName
        );
        if (found && !found.bShow) {
          pico3.bShowTop = false;
          return;
        }
      });
      return;
    }
  }

  public UpdateAllCheck(target: AndonDataType, colIndex: number) {
    switch (target) {
      case "buildings":
        const ib = this.filter.buildings[colIndex].items
          .slice(1)
          .findIndex((e: FilterItem) => !e.bShow);
        // check off の項目がある → すべて選択を off, なければ on
        this.filter.buildings[colIndex].items[0].bShow = ib < 0;
        break;
      case "sensors":
        const is = this.filter.sensors[colIndex].items
          .slice(1)
          .findIndex((e: FilterItem) => !e.bShow);
        // check off の項目がある → すべて選択を off, なければ on
        this.filter.sensors[colIndex].items[0].bShow = is < 0;
        break;
      case "pico3":
        const ip = this.filter.pico3[colIndex].items
          .slice(1)
          .findIndex((e: FilterItem) => !e.bShow);
        // check off の項目がある → すべて選択を off, なければ on
        this.filter.pico3[colIndex].items[0].bShow = ip < 0;
        break;

      default:
        break;
    }
  }

  public Sort(param: AndonSortParam) {
    if (param.target === "buildings") {
      this.sortParamBuilding = param;
      if (param.param === "STATUS") {
        if (param.order === "asc") {
          this.buildings.sort(
            (a: BuildingInfo, b: BuildingInfo) =>
              a.sensorStatus.priority - b.sensorStatus.priority
          );
        } else if (param.order === "desc") {
          this.buildings.sort(
            (a: BuildingInfo, b: BuildingInfo) =>
              b.sensorStatus.priority - a.sensorStatus.priority
          );
        }
      } else if (param.param === "建物名") {
        if (param.order === "asc") {
          this.buildings.sort((a: BuildingInfo, b: BuildingInfo) =>
            a.BuildingName.localeCompare(b.BuildingName, "ja")
          );
        } else if (param.order === "desc") {
          this.buildings.sort((a: BuildingInfo, b: BuildingInfo) =>
            b.BuildingName.localeCompare(a.BuildingName, "ja")
          );
        }
      }
    } else if (param.target === "sensors") {
      this.sortParamSensor = param;
      if (param.param === "STATUS") {
        if (param.order === "asc") {
          this.sensors.sort(
            (a: Sensor, b: Sensor) =>
              a.GetLeakageExpression().priority -
              b.GetLeakageExpression().priority
          );
        } else if (param.order === "desc") {
          this.sensors.sort(
            (a: Sensor, b: Sensor) =>
              b.GetLeakageExpression().priority -
              a.GetLeakageExpression().priority
          );
        }
      } else if (param.param === "更新日時") {
        if (param.order === "asc") {
          this.sensors.sort((a: Sensor, b: Sensor) =>
            a.StatusUpdatedAt().localeCompare(b.StatusUpdatedAt(), "ja")
          );
        } else if (param.order === "desc") {
          this.sensors.sort((a: Sensor, b: Sensor) =>
            b.StatusUpdatedAt().localeCompare(a.StatusUpdatedAt(), "ja")
          );
        }
      } else if (param.param === "端末名称") {
        if (param.order === "asc") {
          this.sensors.sort((a: Sensor, b: Sensor) =>
            a.SensorName.localeCompare(b.SensorName, "ja")
          );
        } else if (param.order === "desc") {
          this.sensors.sort((a: Sensor, b: Sensor) =>
            b.SensorName.localeCompare(a.SensorName, "ja")
          );
        }
      } else if (param.param === "建物名") {
        if (param.order === "asc") {
          this.sensors.sort((a: Sensor, b: Sensor) =>
            a.BuildingName.localeCompare(b.BuildingName, "ja")
          );
        } else if (param.order === "desc") {
          this.sensors.sort((a: Sensor, b: Sensor) =>
            b.BuildingName.localeCompare(a.BuildingName, "ja")
          );
        }
      } else if (param.param === "設置場所") {
        if (param.order === "asc") {
          this.sensors.sort((a: Sensor, b: Sensor) =>
            a.LocationName.localeCompare(b.LocationName, "ja")
          );
        } else if (param.order === "desc") {
          this.sensors.sort((a: Sensor, b: Sensor) =>
            b.LocationName.localeCompare(a.LocationName, "ja")
          );
        }
      }
    } else if (param.target === "pico3") {
      this.sortParamPico3 = param;
      if (param.param === "STATUS") {
        if (param.order === "asc") {
          this.pico3.sort(
            (a: Pico3, b: Pico3) =>
              a.GetLeakageExpression().priority -
              b.GetLeakageExpression().priority
          );
        } else if (param.order === "desc") {
          this.pico3.sort(
            (a: Pico3, b: Pico3) =>
              b.GetLeakageExpression().priority -
              a.GetLeakageExpression().priority
          );
        }
      } else if (param.param === "更新日時") {
        if (param.order === "asc") {
          this.pico3.sort((a: Pico3, b: Pico3) =>
            a.StatusUpdatedAt().localeCompare(b.StatusUpdatedAt(), "ja")
          );
        } else if (param.order === "desc") {
          this.pico3.sort((a: Pico3, b: Pico3) =>
            b.StatusUpdatedAt().localeCompare(a.StatusUpdatedAt(), "ja")
          );
        }
      } else if (param.param === "端末名称") {
        if (param.order === "asc") {
          this.pico3.sort((a: Pico3, b: Pico3) =>
            a.PicoName.localeCompare(b.PicoName, "ja")
          );
        } else if (param.order === "desc") {
          this.pico3.sort((a: Pico3, b: Pico3) =>
            b.PicoName.localeCompare(a.PicoName, "ja")
          );
        }
      } else if (param.param === "建物名") {
        if (param.order === "asc") {
          this.pico3.sort((a: Pico3, b: Pico3) =>
            a.BuildingName.localeCompare(b.BuildingName, "ja")
          );
        } else if (param.order === "desc") {
          this.pico3.sort((a: Pico3, b: Pico3) =>
            b.BuildingName.localeCompare(a.BuildingName, "ja")
          );
        }
      } else if (param.param === "設置場所") {
        if (param.order === "asc") {
          this.pico3.sort((a: Pico3, b: Pico3) =>
            a.LocationName.localeCompare(b.LocationName, "ja")
          );
        } else if (param.order === "desc") {
          this.pico3.sort((a: Pico3, b: Pico3) =>
            b.LocationName.localeCompare(a.LocationName, "ja")
          );
        }
      }
    }
  }
}
